import { Reveal } from '@/components/motion/Reveal'
import { SplitText } from '@/components/motion/SplitText'
import { useSite } from '@/app/site-context'
import { interpolate } from '@/i18n'
import type { PolicyBlock, PolicyKind } from '@/i18n/dictionary'
import { LOCALE_META } from '@/i18n/locales'
import type { Locale } from '@/i18n/locales'
import { seatLine, siteConfig } from '@/lib/site-config'

interface PolicyPageProps {
  kind: PolicyKind
  related: readonly PolicyKind[]
  onNavigate: (href: string) => void
}

const TEXT_LOCALES: readonly Locale[] = ['en', 'hr']

const COUNTRY_NAMES: Partial<Record<string, string>> = { hr: 'Hrvatska', tr: 'Hırvatistan' }

const isSparsa = (kind: PolicyKind): boolean => kind.startsWith('sparsa')

const blockKey = (block: PolicyBlock): string => {
  if (typeof block === 'string') return block.slice(0, 48)
  if ('list' in block) return `list:${block.list[0] ?? ''}`.slice(0, 48)
  return `quote:${block.quote[0] ?? ''}`.slice(0, 48)
}

function PolicyBlockView({
  block,
  values,
}: {
  block: PolicyBlock
  values: Record<string, string>
}) {
  if (typeof block === 'string') {
    return <p className="prose__paragraph">{interpolate(block, values)}</p>
  }
  if ('list' in block) {
    return (
      <ul className="prose__list">
        {block.list.map((item) => (
          <li key={item.slice(0, 48)}>{interpolate(item, values)}</li>
        ))}
      </ul>
    )
  }
  return (
    <blockquote className="prose__quote">
      {block.quote.map((item) => (
        <p key={item.slice(0, 48)}>{interpolate(item, values)}</p>
      ))}
    </blockquote>
  )
}

export function PolicyPage({ kind, related, onNavigate }: PolicyPageProps) {
  const { dictionary, pathFor, locale } = useSite()
  const policy = dictionary[kind]
  const home = pathFor('home')
  const linkLabels = { ...dictionary.mediusLinks, ...dictionary.sparsaLinks }
  const values = {
    provider: `${siteConfig.legalName} (MBO ${siteConfig.register.mbo})`,
    email: isSparsa(kind) ? siteConfig.sparsa.email : siteConfig.email,
    abuseEmail: siteConfig.sparsa.abuseEmail,
    address: seatLine(COUNTRY_NAMES[policy.contentLang] ?? 'Croatia'),
  }
  const contentLang =
    policy.contentLang === LOCALE_META[locale].htmlLang ? undefined : policy.contentLang
  const titleId = `${kind}-title`

  const follow = (event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
    event.preventDefault()
    onNavigate(href)
  }

  return (
    <section className="prose section" aria-labelledby={titleId}>
      <h1 className="prose__title" id={titleId} lang={contentLang}>
        <SplitText text={policy.title} stagger={30} />
      </h1>
      <p className="prose__meta">
        {policy.lastUpdatedLabel}: <time dateTime={policy.lastUpdated}>{policy.lastUpdated}</time>
      </p>
      <p className="prose__meta policy__languages">
        {policy.languageNote}{' '}
        {TEXT_LOCALES.filter((item) => item !== locale).map((item) => (
          <a
            key={item}
            href={pathFor(kind, item)}
            hrefLang={LOCALE_META[item].hrefLang}
            lang={LOCALE_META[item].htmlLang}
            className="policy__language-link"
            onClick={(event) => {
              follow(event, pathFor(kind, item))
            }}
          >
            {LOCALE_META[item].endonym}
          </a>
        ))}
      </p>

      <div lang={contentLang}>
        <Reveal variant="rise">
          <p className="prose__lede">{interpolate(policy.intro, values)}</p>
        </Reveal>

        {policy.sections.map((section) => (
          <Reveal variant="rise" key={section.heading}>
            <h2 className="prose__heading">{section.heading}</h2>
            {section.paragraphs.map((block) => (
              <PolicyBlockView key={blockKey(block)} block={block} values={values} />
            ))}
          </Reveal>
        ))}
      </div>

      <div className="policy__actions">
        {related.map((target, index) => (
          <a
            key={target}
            className={index === 0 ? 'button button--solid' : 'button button--ghost'}
            href={pathFor(target)}
            onClick={(event) => {
              follow(event, pathFor(target))
            }}
          >
            {linkLabels[target]}
          </a>
        ))}
        <a
          className="button button--ghost"
          href={home}
          onClick={(event) => {
            follow(event, home)
          }}
        >
          {policy.backHome}
        </a>
      </div>
    </section>
  )
}
